import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { routes } from "@/lib/routes";
import { Bell, MapPin, Clock, MessageSquare, Star, Users, CalendarCheck } from "lucide-react";

const getNotificationIcon = (type: string) => {
  switch (type) {
    case "booking_confirmed":
    case "booking_reminder":
    case "session_reminder":
      return <CalendarCheck className="h-4 w-4" />;
    case "check_in_reminder":
    case "session_starting":
      return <Clock className="h-4 w-4" />;
    case "location_update":
    case "coach_available":
      return <MapPin className="h-4 w-4" />;
    case "new_message":
    case "message":
      return <MessageSquare className="h-4 w-4" />;
    case "review_reminder":
      return <Star className="h-4 w-4" />;
    case "group_update":
    case "invite_update":
      return <Users className="h-4 w-4" />;
    default:
      return <Bell className="h-4 w-4" />;
  }
};

const getActionLabel = (type: string): string | null => {
  switch (type) {
    case "booking_confirmed":
    case "booking_reminder":
    case "session_reminder":
    case "session_starting":
      return "View Session";
    case "check_in_reminder":
      return "Check In";
    case "new_message":
    case "message":
      return "Open Chat";
    case "review_reminder":
      return "Leave Review";
    case "coach_available":
    case "new_experience":
      return "View";
    default:
      return null;
  }
};

const isUrgent = (type: string): boolean => {
  return [
    "check_in_reminder",
    "session_starting",
    "session_reminder",
    "booking_reminder",
  ].includes(type);
};

export const GlobalNotificationPopup = () => {
  const navigateRef = useRef<ReturnType<typeof useNavigate>>(useNavigate());
  // Update ref on each render so we always have current navigate
  navigateRef.current = useNavigate();

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setup = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return;

      channel = supabase
        .channel("global-notification-popups")
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "notifications",
            filter: `user_id=eq.${session.user.id}`,
          },
          (payload) => {
            const n = payload.new as {
              id: string;
              title: string;
              message: string;
              type: string;
              link: string | null;
              metadata: any;
            };

            const urgent = isUrgent(n.type);
            const actionLabel = getActionLabel(n.type);
            const link = n.link || n.metadata?.url;
            const icon = getNotificationIcon(n.type);

            toast(n.title, {
              description: n.message,
              icon,
              duration: urgent ? 12000 : 6000,
              position: "top-center",
              action: actionLabel && link
                ? {
                    label: actionLabel,
                    onClick: () => navigateRef.current(link),
                  }
                : undefined,
              className: urgent
                ? "border-destructive/50 bg-destructive/5"
                : undefined,
            });
          }
        )
        .subscribe();
    };

    setup();

    // Listen for auth changes to resubscribe
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      (_event) => {
        if (channel) {
          supabase.removeChannel(channel);
          channel = null;
        }
        setup();
      }
    );

    // Reconnect on visibility change (iOS WebSocket recovery)
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        if (channel) {
          supabase.removeChannel(channel);
          channel = null;
        }
        setup();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      authSub.unsubscribe();
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  return null;
};
